import { useCallback, useRef, useState } from 'react';

import {
  type WorkspaceAgentMentionResultsBySource,
  type WorkspaceAgentMentionSourceKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { getWorkspaceAgentMentionSourceToFollow } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import {
  DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY,
  WORKSPACE_AGENT_MENTION_SOURCE_KEYS,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionSources';

export type UseWorkspaceAgentMentionSourceSelectionParams = {
  isPickerOpen: boolean;
  query: string;
};

export type UseWorkspaceAgentMentionSourceSelectionResult = {
  activeSourceKey: WorkspaceAgentMentionSourceKey;
  /**
   * Moves the picker to a source that matched the query more strongly than the active one. A no-op
   * once the user has pinned a chip: there is no switch left to make.
   */
  autoSwitchToSourceWithHits: (resultsBySource: WorkspaceAgentMentionResultsBySource) => void;
  /** The user picked a chip for this mention, so nothing may move the picker off it. */
  isSourcePinned: boolean;
  onSourceChange: (sourceKey: WorkspaceAgentMentionSourceKey) => void;
  /** The picker closed: the pin and any auto-switch are given up, a hand-picked chip is not. */
  resetForNewMention: () => void;
};

/**
 * Which source the picker is on, and what is allowed to change it. Two lifetimes for one gesture:
 * the tab a user picked by hand is what the next `@` opens on, so it lives as long as the composer;
 * the pin that stops the picker following the hits only holds for the mention being written, or one
 * pick would disable auto-switching for good.
 */
export const useWorkspaceAgentMentionSourceSelection = ({
  isPickerOpen,
  query,
}: UseWorkspaceAgentMentionSourceSelectionParams): UseWorkspaceAgentMentionSourceSelectionResult => {
  const [activeSourceKey, setActiveSourceKey] = useState<WorkspaceAgentMentionSourceKey>(
    DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY,
  );
  /**
   * What a hand-picked chip left behind, so the next `@` opens where the last one was left. A ref,
   * not state: nothing renders off it, and `resetForNewMention` has to stay stable — the suggestion
   * plugin is configured once, so a new identity would never reach the live editor.
   */
  const stickySourceKeyRef = useRef<WorkspaceAgentMentionSourceKey>(DEFAULT_WORKSPACE_AGENT_MENTION_SOURCE_KEY);
  /** State, not a ref: the callers' `enabled` flags are read during render, not from a callback. */
  const [isSourcePinned, setIsSourcePinned] = useState(false);

  /** A chip press pins the source: auto-switch stops here, and the next `@` opens on it. */
  const onSourceChange = useCallback((sourceKey: WorkspaceAgentMentionSourceKey) => {
    setIsSourcePinned(true);
    stickySourceKeyRef.current = sourceKey;
    setActiveSourceKey(sourceKey);
  }, []);

  const resetForNewMention = useCallback(() => {
    setIsSourcePinned(false);
    // An auto-switch belongs to the mention it was made for; a manual pick outlives it.
    setActiveSourceKey(stickySourceKeyRef.current);
  }, []);

  const autoSwitchToSourceWithHits = useCallback(
    (resultsBySource: WorkspaceAgentMentionResultsBySource) => {
      if (isSourcePinned || !isPickerOpen || query === '') {
        return;
      }

      const nextSourceKey = getWorkspaceAgentMentionSourceToFollow(
        activeSourceKey,
        resultsBySource,
        WORKSPACE_AGENT_MENTION_SOURCE_KEYS,
      );

      if (nextSourceKey) {
        setActiveSourceKey(nextSourceKey);
      }
    },
    [activeSourceKey, isPickerOpen, isSourcePinned, query],
  );

  return {
    activeSourceKey,
    autoSwitchToSourceWithHits,
    isSourcePinned,
    onSourceChange,
    resetForNewMention,
  };
};
